/**
 * Flight operations · passenger flow: every queue forecast from the flight
 * record and the resource plan, the lanes that keep them short, and the
 * people who will miss their flight if nothing changes.
 *
 * What it does that a queue display does not: it forecasts before the queue
 * forms (show-up from each flight's profile, desks from the allocation, the
 * immigration wave shaped by bridge or bus), it follows people desk →
 * security → passport control, it prescribes the lanes (in time to call the
 * staff, never flickering, costed in officer-hours), and it names the
 * flights at risk — and it is reset by what a sensor or a supervisor counts.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { useAllocAssign, useAllocDay, useFlowAdopt, useFlowCheckpoint, useFlowDay, useFlowObserve } from "../../api/queries";
import { Button, Card, EmptyState, ErrorState, Field, Input, Segmented, Select, Skeleton } from "../../design/components";
import { checkPlacement, type EngineInput, type Resource } from "../../domain/allocation";
import {
  FLOW,
  PROFILE_LABEL,
  STAGE_LABEL,
  recommendedSteps,
  type Checkpoint,
  type CheckpointForecast,
  type DeskForecast,
  type FlowDay,
} from "../../domain/flow";
import { t, tn } from "../../i18n";
import { tx } from "../../i18n/computed";
import { Page } from "../../layout/AppShell";
import { hhmm as clock } from "../../lib/format";
import { Tiles } from "../executive/shared";
import { FlightTheme } from "./FlightTheme";
import s from "./Flights.module.css";
import c from "./Flow.module.css";

type Tab = "checkpoints" | "flights" | "counts";
const hhmm = (iso?: string | null) => clock(iso ?? undefined, "—");

export function FlowPage() {
  const session = useSession();
  const [tab, setTab] = useState<Tab>("checkpoints");
  const day = useFlowDay();
  const d = day.data;
  const f = d?.forecast;
  const worst = f?.checkpoints.reduce<{ code: string; w: number; rw: number } | null>(
    (m, x) => (!m || (x.peak?.waitMin ?? 0) > m.w ? { code: x.code, w: x.peak?.waitMin ?? 0, rw: x.recPeak?.waitMin ?? 0 } : m),
    null,
  );
  const risk = f?.atRisk.reduce((n, x) => n + x.people, 0) ?? 0;
  const riskRec = f?.atRisk.reduce((n, x) => n + x.recPeople, 0) ?? 0;
  const hours = f?.checkpoints.reduce((n, x) => n + x.staffHours, 0) ?? 0;
  const hoursRec = f?.checkpoints.reduce((n, x) => n + x.recStaffHours, 0) ?? 0;

  useScreenContext({
    route: "/passenger-flow",
    title: "Passenger flow",
    summary: f
      ? [
          t("Longest predicted wait {w} min at {code}; {r} min with the recommended lanes.", { w: worst?.w ?? 0, code: worst?.code ?? "", r: worst?.rw ?? 0 }),
          t("{n} people predicted to miss the gate; {r} with the recommended lanes.", { n: risk, r: riskRec }),
        ]
      : [],
    offers: [{ label: t("Who will miss their flight?"), ask: "which departures have passengers predicted to miss the gate, and why?" }],
    worksInFull: true,
  });

  return (
    <FlightTheme>
      <Page eyebrow={t("{site} · Flight operations", { site: session.site.code })} title="Passenger flow">
        <p className={s.lede}>
          {t(
            "Queues forecast before they form, with the lanes and booths needed to keep waits on target.",
          )}
        </p>
        {day.isPending && <Skeleton rows={6} />}
        {day.isError && <ErrorState error={day.error} retry={() => void day.refetch()} />}
        {d && f && f.departures.length === 0 && f.arrivals.length === 0 && (
          <EmptyState>
            {t("No flights in the next few hours, so there are no queues to forecast. Load the schedule on Flight record.")}
          </EmptyState>
        )}
        {d && f && d.checkpoints.length === 0 && <EmptyState>{t("No checkpoints are set up for this site yet.")}</EmptyState>}
        {d && f && <AssumedNote f={f} />}
        {d && f && (
          <>
            <Tiles
              currency="SAR"
              items={[
                {
                  key: "wait",
                  label: t("longest wait ahead"),
                  value: worst?.w ?? 0,
                  unit: t("min"),
                  text: t("at {code}; {r} min with the recommended lanes", { code: worst?.code ?? "", r: worst?.rw ?? 0 }),
                  tone: (worst?.w ?? 0) > 20 ? "bad" : (worst?.w ?? 0) > 10 ? "warn" : "good",
                  assumed: d.sample,
                  sources: [],
                },
                {
                  key: "risk",
                  label: t("may miss the gate"),
                  value: risk,
                  unit: "",
                  text: t("{r} with the recommended lanes", { r: riskRec }),
                  tone: risk ? "bad" : "good",
                  sources: [],
                },
                {
                  key: "desks",
                  label: t("flights short of desks"),
                  value: f.desks.filter((x) => x.needDesks > x.desks).length,
                  unit: "",
                  text: t("check-in wait over {n} min", { n: 20 }),
                  tone: f.desks.some((x) => x.needDesks > x.desks) ? "warn" : "good",
                  sources: [],
                },
                {
                  key: "hours",
                  label: t("officer-hours ahead"),
                  value: Math.round(hours),
                  unit: "",
                  text: t("{r} with the recommended lanes", { r: Math.round(hoursRec) }),
                  sources: [],
                },
              ]}
            />
            <Segmented
              ariaLabel="Passenger flow"
              value={tab}
              onChange={setTab}
              options={[
                { value: "checkpoints", label: "Checkpoints" },
                { value: "flights", label: "Flights" },
                { value: "counts", label: "Counts and settings" },
              ]}
            />
            {tab === "checkpoints" && d.checkpoints.map((cp) => <CheckpointCard key={cp.code} cp={cp} f={f.checkpoints.find((x) => x.code === cp.code)} now={f.now} />)}
            {tab === "flights" && <FlightsTab day={d} />}
            {tab === "counts" && <CountsTab day={d} />}
          </>
        )}
      </Page>
    </FlightTheme>
  );
}

/* ── one checkpoint ───────────────────────────────────────────────────── */

function CheckpointCard({ cp, f, now }: { cp: Checkpoint; f?: CheckpointForecast; now: string }) {
  const adopt = useFlowAdopt();
  const [show, setShow] = useState<"cur" | "rec">("cur");
  if (!f) return null;
  const max = Math.max(cp.targetWaitMin * 2, ...f.slots.map((x) => Math.max(x.waitMin, x.recWaitMin)));
  const nowMs = Date.parse(now);
  const good = f.recMinutesOverTarget < f.minutesOverTarget || f.recStaffHours < f.staffHours;
  return (
    <Card
      title={`${t(cp.name)} · ${cp.code}`}
      sub={t("{stage} · target {t} min · {n} people ahead", { stage: t(STAGE_LABEL[cp.stage]), t: cp.targetWaitMin, n: f.people })}
      testId={`flow-cp-${cp.code}`}
      actions={
        <Segmented
          ariaLabel="Plan"
          value={show}
          onChange={setShow}
          options={[
            { value: "cur", label: "Rostered lanes" },
            { value: "rec", label: "Recommended" },
          ]}
        />
      }
    >
      <div className={c.facts}>
        <span data-bad={(f.peak?.waitMin ?? 0) > cp.targetWaitMin || undefined}>
          {t("rostered: longest wait {w} min at {at}, {o} min over target", { w: f.peak?.waitMin ?? 0, at: hhmm(f.peak?.at), o: f.minutesOverTarget })}
        </span>
        <span data-good={f.recMinutesOverTarget === 0 || undefined}>
          {t("recommended: longest wait {w} min, {o} min over target", { w: f.recPeak?.waitMin ?? 0, o: f.recMinutesOverTarget })}
        </span>
        <span>{t("officer-hours {a} → {b}", { a: f.staffHours, b: f.recStaffHours })}</span>
      </div>
      {f.nowcast && (
        <p className={c.nowcast} data-testid={`nowcast-${cp.code}`}>
          {t("{src} counted {o} in the queue at {at}; the model had {p}. The forecast starts from the count.", {
            src: f.nowcast.source === "sensor" ? t("The sensor") : t("A supervisor"),
            o: f.nowcast.observed,
            at: hhmm(f.nowcast.at),
            p: f.nowcast.predicted,
          })}
        </p>
      )}
      <div className={c.chart}>
        <div className={c.target} style={{ bottom: `${(cp.targetWaitMin / max) * 100}%` }}>
          <span>{t("target {t} min", { t: cp.targetWaitMin })}</span>
        </div>
        {f.slots.map((x) => {
          const w = show === "cur" ? x.waitMin : x.recWaitMin;
          const l = show === "cur" ? x.lanes : x.recLanes;
          const past = Date.parse(x.at) < nowMs;
          return (
            <div
              key={x.at}
              className={c.col}
              data-past={past || undefined}
              data-over={w > cp.targetWaitMin || undefined}
              title={`${hhmm(x.at)} · ${t("wait {w} min", { w })} · ${tn(l, "{n} lane", "{n} lanes")} · ${t("{n} arriving", { n: x.arrivals })}${x.top.length ? ` · ${x.top.map((y) => `${y.label} ${y.n}`).join(", ")}` : ""}`}
            >
              <span className={c.bar} style={{ height: `${Math.min(100, (w / max) * 100)}%` }} />
              <span className={c.lanes} style={{ bottom: `${Math.min(100, (l / cp.lanesMax) * 100)}%` }} />
            </div>
          );
        })}
      </div>
      <div className={c.axis}>
        {f.slots
          .filter((_, i) => i % 12 === 0)
          .map((x) => (
            <span key={x.at}>{hhmm(x.at)}</span>
          ))}
      </div>
      <p className={c.dim}>{t("Bars: the wait for somebody arriving then. Line: lanes open, of {n}.", { n: cp.lanesMax })}</p>
      {f.changes.length > 0 ? (
        <>
          <h4 className={c.h4}>{t("What to change, and when to call the staff")}</h4>
          <ul className={c.changes}>
            {f.changes.map((x) => (
              <li key={x.at} data-up={x.to > x.from || undefined}>
                <b className={c.mono} dir="ltr">
                  {hhmm(x.at)} · {x.from} → {x.to}
                </b>{" "}
                {x.to > x.from && <span className={c.call}>{t("call by {t}", { t: hhmm(x.callBy) })}</span>} <span>{tx(x.why)}</span>
              </li>
            ))}
          </ul>
          <Button
            variant="primary"
            disabled={!good || adopt.isPending}
            data-testid={`flow-adopt-${cp.code}`}
            onClick={() => adopt.mutate({ code: cp.code, steps: recommendedSteps(f) })}
          >
            {t("Adopt the recommended plan")}
          </Button>
          {adopt.isSuccess && <span className={c.ok}> {t("Adopted. The forecast now runs on it.")}</span>}
        </>
      ) : (
        <p className={c.ok}>{t("The rostered plan already holds the target.")}</p>
      )}
    </Card>
  );
}

/* ── build 6: a load nobody entered is an assumption, and says so ─────── */

/** Beside a people figure built on no load: "assumed", with what was assumed on hover. */
function Assumed({ on }: { on?: boolean }) {
  if (!on) return null;
  return (
    <span
      className={c.dim}
      data-testid="flow-assumed"
      title={t("No load on the record: {p}% of the seats is assumed.", { p: Math.round(FLOW.assumedLoadFactor * 100) })}
    >
      {" "}
      {t("assumed")}
    </span>
  );
}

/** How many flights on the forecast run on the seats assumption, and how to replace it. */
function AssumedNote({ f }: { f: FlowDay["forecast"] }) {
  const ids = new Set(
    [...f.departures, ...f.arrivals].filter((x) => x.assumed).map((x) => x.flightId),
  );
  if (!ids.size) return null;
  return (
    <p className={c.dim} data-testid="flow-assumed-note">
      {tn(ids.size, "{n} flight has no load on the record.", "{n} flights have no load on the record.")}{" "}
      {t("{p}% of the seats is assumed for them. Enter loads on Flight record.", {
        p: Math.round(FLOW.assumedLoadFactor * 100),
      })}
    </p>
  );
}

/* ── flights ──────────────────────────────────────────────────────────── */

function FlightsTab({ day }: { day: FlowDay }) {
  const f = day.forecast;
  return (
    <>
      <Card title="Who may miss the gate" sub={t("Passengers at risk of missing their flight")} testId="flow-risk">
        {f.atRisk.length ? (
          <div className={c.scroll}>
            <table className={c.table}>
              <thead>
                <tr>
                  <th>{t("Flight")}</th>
                  <th>{t("Departs")}</th>
                  <th>{t("At risk")}</th>
                  <th>{t("With the recommended lanes")}</th>
                  <th>{t("Why")}</th>
                </tr>
              </thead>
              <tbody>
                {f.atRisk.map((r) => (
                  <tr key={r.flightId}>
                    <td>
                      <b>{r.label}</b>
                    </td>
                    <td className={c.mono}>{hhmm(r.departureAt)}</td>
                    <td className={c.bad}>
                      {r.people}
                      <Assumed on={r.assumed} />
                    </td>
                    <td className={r.recPeople ? c.bad : c.ok}>{r.recPeople}</td>
                    <td className={c.why}>{tx(r.why)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState>{t("Nobody is predicted to miss a gate.")}</EmptyState>
        )}
      </Card>
      <DesksCard desks={f.desks} />
      <Card title="Arrivals at passport control" sub={t("Arriving passengers by stand")} testId="flow-arrivals">
        {f.arrivals.length ? (
          <div className={c.scroll}>
            <table className={c.table}>
              <thead>
                <tr>
                  <th>{t("Flight")}</th>
                  <th>{t("On block")}</th>
                  <th>{t("Stand")}</th>
                  <th>{t("People")}</th>
                  <th>{t("Wait at passport control")}</th>
                </tr>
              </thead>
              <tbody>
                {f.arrivals.map((a) => (
                  <tr key={a.flightId}>
                    <td>
                      <b>{a.label}</b>
                    </td>
                    <td className={c.mono}>{hhmm(a.onBlock)}</td>
                    <td>{a.stand ? `${a.stand} · ${a.bus ? t("bus") : t("bridge")}` : t("no stand yet")}</td>
                    <td className={c.mono}>
                      {a.people}
                      <Assumed on={a.assumed} />
                    </td>
                    <td className={c.mono}>{a.waitMin == null ? t("domestic") : t("{n} min", { n: a.waitMin })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState>{t("No arrivals in the next hours.")}</EmptyState>
        )}
      </Card>
    </>
  );
}

function DesksCard({ desks }: { desks: DeskForecast[] }) {
  const alloc = useAllocDay();
  const assign = useAllocAssign();
  const qc = useQueryClient();
  const [msg, setMsg] = useState<Record<string, string>>({});
  /*
   * A run of desks side by side, free while the flight is open, found with
   * the allocation engine's own checks: the full number the forecast asks
   * for if there is one, else the most there is, and said so.
   */
  const give = (d: DeskForecast) => {
    const day = alloc.data;
    if (!day) return;
    const input: EngineInput = {
      resources: day.resources,
      demands: day.demands,
      current: day.plan,
      rules: day.rules,
      learned: day.learned,
      policies: Object.fromEntries(day.policies.map((p) => [p.kind, p])) as EngineInput["policies"],
      now: new Date(day.now),
      mode: "replan",
    };
    const key = `counter:${d.flightId}`;
    const groups = new Map<string, Resource[]>();
    for (const r of day.resources.filter((x) => x.kind === "counter")) groups.set(r.group ?? "", [...(groups.get(r.group ?? "") ?? []), r]);
    let codes: string[] = [];
    for (let u = d.needDesks; u > d.desks && !codes.length; u--)
      for (const rs of groups.values()) {
        const sorted = [...rs].sort((p, q) => (p.seq ?? 0) - (q.seq ?? 0));
        for (let i = 0; i + u <= sorted.length && !codes.length; i++) {
          const run = sorted.slice(i, i + u).map((x) => x.code);
          if (!checkPlacement(input, key, run).length) codes = run;
        }
        if (codes.length) break;
      }
    if (!codes.length) {
      setMsg((m) => ({ ...m, [d.flightId]: t("No run of more desks is free while it is open.") }));
      return;
    }
    assign.mutate(
      { key: `counter:${d.flightId}`, codes, note: t("sized from the passenger-flow forecast") },
      {
        onSuccess: (out) => {
          setMsg((m) => ({
            ...m,
            [d.flightId]: out.refused.length
              ? t("Refused: {why}", { why: out.refused.map((x) => x.why).join("; ") })
              : codes.length < d.needDesks
                ? t("Given {n} desks ({c}) — the most free side by side; it needed {need}.", { n: codes.length, c: codes.join(", "), need: d.needDesks })
                : t("Given {n} desks ({c}).", { n: codes.length, c: codes.join(", ") }),
          }));
          void qc.invalidateQueries({ queryKey: ["flow"] });
        },
      },
    );
  };
  const rows = desks.filter((d) => d.peakWaitMin > 0 || d.needDesks > d.desks);
  return (
    <Card title="Check-in, flight by flight" sub={t("Queue at each flight's check-in desks")} testId="flow-desks">
      {rows.length ? (
        <div className={c.scroll}>
          <table className={c.table}>
            <thead>
              <tr>
                <th>{t("Flight")}</th>
                <th>{t("Profile")}</th>
                <th>{t("Desks")}</th>
                <th>{t("Open")}</th>
                <th>{t("At the desks")}</th>
                <th>{t("Longest wait")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.flightId} data-testid={`desk-${d.label}`}>
                  <td>
                    <b>{d.label}</b>
                  </td>
                  <td>{t(PROFILE_LABEL[d.profile])}</td>
                  <td className={c.mono}>
                    {d.desks}
                    {!d.allocated && <span className={c.dim}> {t("(not allocated)")}</span>}
                  </td>
                  <td className={c.mono}>
                    {hhmm(d.deskFrom)}–{hhmm(d.deskTo)}
                  </td>
                  <td className={c.mono}>
                    {d.people}
                    <Assumed on={d.assumed} />
                  </td>
                  <td className={d.peakWaitMin > 20 ? c.bad : c.mono}>{t("{n} min", { n: d.peakWaitMin })}</td>
                  <td>
                    {d.needDesks > d.desks && d.allocated ? (
                      <>
                        <Button size="sm" onClick={() => give(d)} disabled={assign.isPending}>
                          {t("Give it {n} desks", { n: d.needDesks })}
                        </Button>
                        {msg[d.flightId] && <div className={c.dim}>{msg[d.flightId]}</div>}
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState>{t("No check-in queue ahead.")}</EmptyState>
      )}
    </Card>
  );
}

/* ── counts and settings ──────────────────────────────────────────────── */

function CountsTab({ day }: { day: FlowDay }) {
  const observe = useFlowObserve();
  const [code, setCode] = useState(day.checkpoints[0]?.code ?? "");
  const [what, setWhat] = useState<"queue" | "wait">("queue");
  const [n, setN] = useState(0);
  const obs = useMemo(() => [...day.observations].sort((a, b) => b.at.localeCompare(a.at)).slice(0, 20), [day.observations]);
  return (
    <>
      <Card title="Count a queue" sub={t("Enter a head count to correct the forecast")} testId="flow-count">
        <div className={c.form}>
          <Field label="Checkpoint">
            <Select value={code} onChange={(e) => setCode(e.target.value)}>
              {day.checkpoints.map((cp) => (
                <option key={cp.code} value={cp.code}>
                  {cp.code} — {t(cp.name)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="What was counted">
            <Select value={what} onChange={(e) => setWhat(e.target.value as "queue" | "wait")}>
              <option value="queue">{t("people in the queue")}</option>
              <option value="wait">{t("minutes of wait")}</option>
            </Select>
          </Field>
          <Field label="Count">
            <Input type="number" min={0} max={5000} value={n} onChange={(e) => setN(Number(e.target.value))} />
          </Field>
          <Button
            variant="primary"
            data-testid="flow-count-save"
            disabled={!code || observe.isPending}
            onClick={() =>
              observe.mutate({ code, at: new Date().toISOString(), ...(what === "queue" ? { queue: n } : { waitMin: n }) })
            }
          >
            {t("Record the count")}
          </Button>
        </div>
        {obs.length > 0 && (
          <ul className={c.changes}>
            {obs.map((o, i) => (
              <li key={i}>
                <b className={c.mono} dir="ltr">{hhmm(o.at)}</b> {o.code} ·{" "}
                {o.queue != null ? t("{n} in the queue", { n: o.queue }) : t("{n} min wait", { n: o.waitMin ?? 0 })} ·{" "}
                {o.source === "sensor" ? t("sensor") : (o.by ?? t("a person"))}
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card title="Checkpoints" sub={t("Lanes, speed, targets, and walking times")} testId="flow-settings">
        <div className={c.settings}>
          {day.checkpoints.map((cp) => (
            <CheckpointEditor key={cp.code} cp={cp} />
          ))}
        </div>
        <p className={c.dim}>
          {t("Domestic is decided by the other airport: {list}", { list: day.domesticNodes.slice(0, 12).join(", ") + (day.domesticNodes.length > 12 ? " …" : "") })}
        </p>
      </Card>
      <Card title="Every change" sub={t("counts, plans adopted and settings, with who")} testId="flow-log">
        {day.log.length ? (
          <ul className={c.changes}>
            {day.log.slice(0, 40).map((l, i) => (
              <li key={i}>
                <b className={c.mono} dir="ltr">{hhmm(l.at)}</b> {l.code} · {l.what} · {l.by ?? "—"}
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState>{t("No changes yet today.")}</EmptyState>
        )}
      </Card>
    </>
  );
}

function CheckpointEditor({ cp }: { cp: Checkpoint }) {
  const save = useFlowCheckpoint();
  const [v, setV] = useState(cp);
  const dirty = JSON.stringify(v) !== JSON.stringify(cp);
  const num = (k: keyof Checkpoint) => (e: React.ChangeEvent<HTMLInputElement>) => setV({ ...v, [k]: Number(e.target.value) });
  return (
    <div className={c.editor} data-testid={`cp-edit-${cp.code}`}>
      <h4>
        {cp.code} · {t(cp.name)}
      </h4>
      <div className={c.nums}>
        <Field label="Lanes at most">
          <Input type="number" min={1} max={60} value={v.lanesMax} onChange={num("lanesMax")} />
        </Field>
        <Field label="People per lane an hour">
          <Input type="number" min={10} max={600} value={v.ratePerLane} onChange={num("ratePerLane")} />
        </Field>
        <Field label="Target wait (min)">
          <Input type="number" min={1} max={120} value={v.targetWaitMin} onChange={num("targetWaitMin")} />
        </Field>
        <Field label="Staff per lane">
          <Input type="number" min={1} max={10} value={v.staffPerLane} onChange={num("staffPerLane")} />
        </Field>
      </div>
      <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate(v)}>
        {t("Save")}
      </Button>
    </div>
  );
}
